import {
  Box,
  Divider,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  useToast,
} from "@chakra-ui/react";
import { FiMoreVertical } from "react-icons/fi";
import { format } from "date-fns";
import CDS from "design-system";

import SideDrawer from "components/SideDrawer";
import useConfirmation from "hooks/useConfirmation";
import FieldRenderer from "../Fields/FieldRenderer";
import { useCustomFormResponseQuery, useDeleteCustomFormResponse } from "../hooks";
import { findAnswer } from "../types";
import type { CustomFormResponse } from "../types";

interface Props {
  responseId: number | string | undefined;
  isOpen: boolean;
  onClose: () => void;
}

export default function ResponseDetailDrawer({ responseId, isOpen, onClose }: Props) {
  const { data: response, isLoading } = useCustomFormResponseQuery(responseId ?? null);
  const { mutate: deleteResponse } = useDeleteCustomFormResponse();
  const { confirm, ConfirmationDialog } = useConfirmation();
  const toast = useToast();

  const openDeleteConfirm = () => {
    if (!response) return;
    confirm({
      header: "Delete this response?",
      body: "This response will be removed from lists and CSV exports. This can't be undone.",
      confirmButtonText: "Delete",
      intent: "warning",
      onConfirm: () => {
        deleteResponse(response.id, {
          onSuccess: () => {
            toast({ title: "Response deleted", status: "success" });
            onClose();
          },
          onError: () => toast({ title: "Delete failed", status: "error" }),
        });
      },
    });
  };

  return (
    <>
      <SideDrawer<CustomFormResponse>
        isOpen={isOpen}
        onClose={onClose}
        item={response}
        isLoading={isLoading}
        size="md"
        header={({ item }) => (
          <Flex justify="space-between" align="center" w="100%">
            <Stack spacing={0}>
              <CDS.Text18 fontWeight="bold">
                {item?.submittedBy?.fullName ?? "Response"}
              </CDS.Text18>
              {item?.customForm?.name && (
                <CDS.Text12 color="fg.subtle" fontWeight="normal">
                  {item.customForm.name}
                </CDS.Text12>
              )}
            </Stack>
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="More actions"
                icon={<FiMoreVertical />}
                variant="ghost"
                size="sm"
                mr={8}
                isDisabled={!item}
              />
              <MenuList>
                <MenuItem color="red.500" onClick={openDeleteConfirm}>
                  Delete response
                </MenuItem>
              </MenuList>
            </Menu>
          </Flex>
        )}
      >
        {({ item }) => {
          if (!item) return isLoading ? <Spinner /> : null;
          return (
            <Stack spacing={0} divider={<Divider />}>
              <Box pb={4}>
                <CDS.Text12 color="fg.subtle">
                  Submitted {format(new Date(item.submittedAt), "MMM d, yyyy 'at' h:mm a")}
                </CDS.Text12>
                {item.submittedBy?.workEmail && (
                  <CDS.Text12 color="fg.subtle">{item.submittedBy.workEmail}</CDS.Text12>
                )}
              </Box>
              {item.fieldsSnapshot.map((field) => (
                <Box key={field.uuid} py={4}>
                  <FieldRenderer
                    field={field}
                    mode="readonly"
                    value={findAnswer(item.answers, field.uuid)}
                  />
                </Box>
              ))}
            </Stack>
          );
        }}
      </SideDrawer>
      <ConfirmationDialog />
    </>
  );
}
